import { useCallback } from 'react';

import type { PageTreeNode } from './pageTreeTypes.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useImperativeQueryInterop as useImperativeQuery } from '@confluence/apollo-relay-interop/entry-points/useImperativeQueryInterop.ts';
import { extractChildren } from './pageTreeUtils.ts';
import PageTreeMacroChildrenQuery from './queries/__generated__/PageTreeMacroChildrenQuery.graphql.ts';
import type {
	PageTreeMacroChildrenQuery$data,
	PageTreeMacroChildrenQuery$variables,
} from './queries/__generated__/PageTreeMacroChildrenQuery.graphql.ts';

/**
 * Per-node hook for fetching children. Each component instance gets its own
 * useImperativeQuery (its own refetch / observable), so concurrent calls
 * from different nodes don't share state or overwrite each other's results.
 */
export const useNodeChildren = (
	sort: string,
	reverse: boolean,
): ((parentId: string) => Promise<PageTreeNode[]>) => {
	const refetchChildren = useImperativeQuery<
		PageTreeMacroChildrenQuery$data,
		PageTreeMacroChildrenQuery$variables
	>(PageTreeMacroChildrenQuery);

	return useCallback(
		(parentId: string): Promise<PageTreeNode[]> => {
			return extractChildren(refetchChildren, parentId, sort, reverse);
		},
		[refetchChildren, sort, reverse],
	);
};
